import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { faCaretDown, faCaretUp, faSort, faXmark } from '@fortawesome/free-solid-svg-icons';
import { DataAnalysisApiService } from '../../services/api/data-analysis-api.service';
import { UtilService } from '../../../../core/services/util/util.service';
import { PlatformService } from '../../../../core/services/platform/platform.service';

interface RestockRow {
  sku: string;
  name: string;
  branchRef: string;
  branchName: string;
  branchStock: number;
  hqStock: number;
  totalQty: number;
  avgPerDay: number;
  daysLeft: number | null;
}

@Component({
  selector: 'app-data-analysis-restock',
  templateUrl: './data-analysis-restock.component.html',
  styleUrl: './data-analysis-restock.component.css'
})
export class DataAnalysisRestockComponent implements OnInit {

  faXmark = faXmark;
  faCaretDown = faCaretDown;
  faCaretUp = faCaretUp;
  faSort = faSort;

  sortField: 'total_qty' | 'avg_per_day' | 'branch_stock' | 'hq_stock' | 'days_left' = 'total_qty';
  sortDir: 'asc' | 'desc' = 'desc';

  channels: any[] = [];
  channelSelected: string = '';
  branchKeyword: string = '';
  branchesFilter: any[] = [];
  showBranchFilter: boolean = false;

  keyword = '';
  productsFilter: Array<{ sku: string; name: string }> = [];
  showProductFilter = false;
  productSelected: Array<{ sku: string; name: string }> = [];

  selectedDate: { type: string, value: string } = { type: 'daypass', value: '30' };

  rows: RestockRow[] = [];
  loading = false;

  countDataSource = 0;
  pageSize = 10;
  pageIndex = 0;
  limit = 10;
  offset = 0;

  form: FormGroup;

  constructor(
    private dataAnalysisApiService: DataAnalysisApiService,
    private utilService: UtilService,
    private platformService: PlatformService,
    private fb: FormBuilder,
  ) {
    this.form = this.fb.group({
      date: this.computeDateRange(this.selectedDate),
    });
  }

  ngOnInit(): void {
    this.channels = (this.platformService?.channels || []).filter(c => c.is_branch);
    this.branchesFilter = this.channels;
    if (this.channels.length > 0) {
      this.channelSelected = this.channels[0].shop_ref;
      this.branchKeyword = this.channels[0].platform_name;
    }
    this.fetchData();
  }

  filterBranches() {
    const k = (this.branchKeyword || '').toLowerCase();
    this.branchesFilter = this.channels.filter(c =>
      (c.platform_name || '').toLowerCase().includes(k)
    );
    this.showBranchFilter = true;
  }

  selectBranch(branch: any) {
    this.channelSelected = branch.shop_ref;
    this.branchKeyword = branch.platform_name;
    this.branchesFilter = this.channels;
    this.showBranchFilter = false;
  }

  openBranchFilter() {
    this.branchesFilter = this.channels;
    this.showBranchFilter = true;
  }

  closeBranchFilter() {
    setTimeout(() => {
      this.showBranchFilter = false;
      const current = this.channels.find(c => c.shop_ref === this.channelSelected);
      if (current) this.branchKeyword = current.platform_name;
    }, 150);
  }

  handleOptionSelected(d: { type: string, value: string }) {
    this.selectedDate = d;
    this.form.get('date')?.setValue(this.computeDateRange(d));
  }

  private computeDateRange(option: { type: string, value: string }): string {
    const dayjs = this.utilService.dayjs;
    if (option.type === 'date-range') {
      const [s, e] = option.value.split(',');
      return `${dayjs(s).format('YYYY-MM-DD')},${dayjs(e).format('YYYY-MM-DD')}`;
    }
    if (option.value === '0') {
      const d = dayjs().format('YYYY-MM-DD');
      return `${d},${d}`;
    }
    if (option.value === '1') {
      const d = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
      return `${d},${d}`;
    }
    if (option.value === 'month') {
      const start = dayjs().startOf('month').format('YYYY-MM-DD');
      const end = dayjs().format('YYYY-MM-DD');
      return `${start},${end}`;
    }
    const start = dayjs().subtract(parseInt(option.value), 'day').format('YYYY-MM-DD');
    const end = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    return `${start},${end}`;
  }

  async fetchData() {
    if (this.loading) return;
    this.loading = true;
    const value = this.form.getRawValue();

    const branchRef = this.channelSelected;
    if (!branchRef) {
      this.rows = [];
      this.countDataSource = 0;
      this.loading = false;
      return;
    }

    const filter: any = {
      limit: this.limit,
      offset: this.offset,
      sort_field: this.sortField,
      sort_by: this.sortDir === 'desc' ? 'max' : 'min',
      branch_ref: branchRef,
    };
    if (value.date) filter.date = value.date;
    if (this.productSelected.length > 0) filter.sku = this.productSelected.map(e => e.sku).join();

    try {
      const res: any = await this.dataAnalysisApiService.getRestockReport(filter);
      if (!res?.success) {
        this.rows = [];
        this.countDataSource = 0;
        return;
      }

      const branch = this.channels.find(c => c.shop_ref === branchRef);
      const branchName = branch?.platform_name || '';

      this.countDataSource = res.data?.count || 0;
      const apiRows: Array<{
        sku: string; name: string; branch_stock: number; hq_stock: number;
        total_qty: number; total_amount: number; avg_per_day: number; days_left: number | null;
      }> = res.data?.rows || [];

      this.rows = apiRows.map(r => ({
        sku: r.sku,
        name: r.name,
        branchRef,
        branchName,
        branchStock: r.branch_stock ?? 0,
        hqStock: r.hq_stock ?? 0,
        totalQty: r.total_qty ?? 0,
        avgPerDay: r.avg_per_day ?? 0,
        daysLeft: r.days_left,
      }));
    } catch {
      this.rows = [];
      this.countDataSource = 0;
    } finally {
      this.loading = false;
    }
  }

  async productSearch() {
    if (this.keyword.length > 2) {
      try {
        const uniquesku = this.productSelected.map(e => e.sku);
        const res: any = await this.dataAnalysisApiService.searchProduct({ keyword: this.keyword, limit: 20 });
        const results = res?.success ? res.data : [];
        this.productsFilter = (results || []).filter((e: any) => !uniquesku.includes(e.sku));
        this.showProductFilter = true;
      } catch {
        this.productsFilter = [];
        this.showProductFilter = false;
      }
    } else {
      this.showProductFilter = false;
    }
  }

  selectProduct(product: any) {
    this.productSelected.push(product);
    this.productsFilter = this.productsFilter.filter((e: any) => e.sku !== product.sku);
    if (this.productsFilter.length === 0) this.showProductFilter = false;
  }

  removeProductSelected(product: any) {
    this.productSelected = this.productSelected.filter(e => e.sku !== product.sku);
  }

  removeProductSelectedAll() {
    this.productSelected = [];
  }

  clearSearch() {
    this.keyword = '';
    this.showProductFilter = false;
  }

  callBackPageChange = (data: any): void => {
    if (this.offset !== data.offset || this.limit !== data.limit) {
      this.offset = data.offset;
      this.limit = data.limit;
      this.fetchData();
    }
    if (data.pageIndex !== undefined) this.pageIndex = data.pageIndex;
    if (data.limit !== undefined) this.pageSize = data.limit;
  }

  submitForm() {
    this.offset = 0;
    this.pageIndex = 0;
    this.fetchData();
  }

  sortBy(field: 'total_qty' | 'avg_per_day' | 'branch_stock' | 'hq_stock' | 'days_left') {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir = 'desc';
    }
    this.offset = 0;
    this.pageIndex = 0;
    this.fetchData();
  }
}
